import React, { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  FindAllControlStatisticsResponse,
  FindAllReportTextTemplatesResponse,
  FindOneControlResponse,
} from '@ansvar-sakerhet/api-client';
import {
  ACTIVE_PERSONDATA_TOPIC_IDS_V1,
  OverwriteMode,
  planReportTextDraft,
  projectStatisticsPlaceholders,
  ReportTextFieldId,
} from '../../../../utils/reportTextGeneration';
import { formatControlDateTime } from '../../../../utils/reportTextTemplates';
import { normalizeForQuill } from '../../../../utils/normalizeForQuill';
import { ReportTextTemplateKey } from '../../../../../report-templates/reportTextTemplateKeys';
import { ResultFormValues } from '../../../../components/ControlLayout/hooks/useResultForm';
import { S } from './GenerateReportTextDialog.styles';

type Props = {
  open: boolean;
  onClose: () => void;
  control: FindOneControlResponse;
  statistics: FindAllControlStatisticsResponse;
  templates: FindAllReportTextTemplatesResponse[];
  currentValues: ResultFormValues;
  onApply: (writes: Partial<Record<ReportTextFieldId, string>>) => void;
};

const buildPlaceholders = (
  control: FindOneControlResponse,
  statistics: FindAllControlStatisticsResponse
) =>
  projectStatisticsPlaceholders(
    {
      date: formatControlDateTime(control.date),
      completedAt: control.completedAt
        ? formatControlDateTime(control.completedAt)
        : 'AVSLUTNINGSTID SAKNAS',
      projectName: control.project.name.trim(),
      address: control.address.trim(),
      city: control.city.name.trim(),
      clientName: control.client.name,
      mainContractor: control.mainContractor.trim(),
    },
    {
      numberOfPersonnel: statistics.control.numberOfPersonnel,
      numberOfControlledPersonnel:
        statistics.control.numberOfControlledPersonnel,
      numberOfCheckedInAndControlledPersonnel:
        statistics.control.numberOfCheckedInAndControlledPersonnel,
      percentageOfCheckedInAndControlledPersonnel:
        statistics.control.percentageOfCheckedInAndControlledPersonnel,
      numberOfNotControlledPersonnel:
        statistics.control.numberOfNotControlledPersonnel,
      percentageOfNotControlledPersonnel:
        statistics.control.percentageOfNotControlledPersonnel,
      numberOfAdditionalPersonnel:
        statistics.control.numberOfAdditionalPersonnel,
      percentageOfAdditionalPersonnel:
        statistics.control.percentageOfAdditionalPersonnel,
      numberOfPersonnelWithInvalidIdentification:
        statistics.control.numberOfPersonnelWithInvalidIdentification,
      percentageOfPersonnelWithInvalidIdentification:
        statistics.control.percentageOfPersonnelWithInvalidIdentification,
      numberOfPersonnelWithIdCardDeviations:
        statistics.control.numberOfPersonnelWithIdCardDeviations,
      percentageOfPersonnelWithIdCardDeviations:
        statistics.control.percentageOfPersonnelWithIdCardDeviations,
      numberOfPersonnelWithoutSafetyEquipment:
        statistics.control.numberOfPersonnelWithoutSafetyEquipment,
      percentageOfPersonnelWithoutSafetyEquipment:
        statistics.control.percentageOfPersonnelWithoutSafetyEquipment,
      numberOfPersonnelWithoutWorkPermit:
        statistics.control.numberOfPersonnelWithoutWorkPermit,
      percentageOfPersonnelWithoutWorkPermit:
        statistics.control.percentageOfPersonnelWithoutWorkPermit,
      numberOfPersonnelWithoutCertificate:
        statistics.control.numberOfPersonnelWithoutCertificate,
      percentageOfPersonnelWithoutCertificate:
        statistics.control.percentageOfPersonnelWithoutCertificate,
    }
  );

const templatesByKey = (
  templates: FindAllReportTextTemplatesResponse[]
): Partial<Record<ReportTextTemplateKey, string>> => {
  const map: Partial<Record<ReportTextTemplateKey, string>> = {};
  for (const template of templates) {
    map[template.key as ReportTextTemplateKey] = template.body;
  }
  return map;
};

export const GenerateReportTextDialog: React.FC<Props> = (props) => {
  const [overwriteMode, setOverwriteMode] =
    useState<OverwriteMode>('only-empty');
  const [expandedPreviews, setExpandedPreviews] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (props.open) {
      setOverwriteMode('only-empty');
      setExpandedPreviews({});
    }
  }, [props.open]);

  const plan = useMemo(() => {
    if (!props.open) {
      return { writes: [], skips: [] };
    }

    return planReportTextDraft({
      templates: templatesByKey(props.templates),
      placeholders: buildPlaceholders(props.control, props.statistics),
      currentValues: {
        workRegisterResult: props.currentValues.workRegisterResult,
        workRegisterActions: props.currentValues.workRegisterActions,
        identificationResult: props.currentValues.identificationResult,
        identificationActions: props.currentValues.identificationActions,
        id06DeviationsResult: props.currentValues.id06DeviationsResult,
        id06DeviationsActions: props.currentValues.id06DeviationsActions,
        safetyEquipmentResult: props.currentValues.safetyEquipmentResult,
        safetyEquipmentActions: props.currentValues.safetyEquipmentActions,
        workPermitResult: props.currentValues.workPermitResult,
        workPermitActions: props.currentValues.workPermitActions,
        secondmentRegulationsResult:
          props.currentValues.secondmentRegulationsResult,
        secondmentRegulationsActions:
          props.currentValues.secondmentRegulationsActions,
        shellProtectionResult: props.currentValues.shellProtectionResult,
        shellProtectionActions: props.currentValues.shellProtectionActions,
        workEnvironmentResult: props.currentValues.workEnvironmentResult,
        workEnvironmentActions: props.currentValues.workEnvironmentActions,
        preApprovedSubcontractorResult:
          props.currentValues.preApprovedSubcontractorResult,
        preApprovedSubcontractorActions:
          props.currentValues.preApprovedSubcontractorActions,
        intoxicationResult: props.currentValues.intoxicationResult,
        intoxicationActions: props.currentValues.intoxicationActions,
        otherResult: props.currentValues.otherResult,
        otherActions: props.currentValues.otherActions,
      },
      overwriteMode,
      activeTopicIds: ACTIVE_PERSONDATA_TOPIC_IDS_V1,
    });
  }, [
    props.open,
    props.templates,
    props.control,
    props.statistics,
    props.currentValues,
    overwriteMode,
  ]);

  const togglePreview = (fieldId: string) => {
    setExpandedPreviews((prev) => ({ ...prev, [fieldId]: !prev[fieldId] }));
  };

  const handleConfirm = () => {
    const writes: Partial<Record<ReportTextFieldId, string>> = {};
    for (const entry of plan.writes) {
      writes[entry.fieldId] = normalizeForQuill(entry.html);
    }
    props.onApply(writes);
    props.onClose();
  };

  if (!props.open) {
    return null;
  }

  return (
    <Dialog.Root
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <Dialog.Portal>
        <S.Overlay>
          <S.Content>
            <S.ContentHeader>
              <Dialog.Title asChild>
                <S.H4>Generera rapporttext</S.H4>
              </Dialog.Title>
              <S.CloseButton asChild onClick={props.onClose}>
                <S.Icon src="Close" width={20} height={20} />
              </S.CloseButton>
            </S.ContentHeader>

            <Dialog.Description asChild>
              <S.Description>
                Förhandsgranska vad som skrivs till Resultat/åtgärder. Texten
                fylls i formuläret — spara kontrollen som vanligt efteråt.
              </S.Description>
            </Dialog.Description>

            <S.Section>
              <S.H6>Skriv över</S.H6>
              <S.OverwriteRow>
                <S.RadioRow>
                  <input
                    type="radio"
                    name="overwriteMode"
                    value="only-empty"
                    checked={overwriteMode === 'only-empty'}
                    onChange={() => setOverwriteMode('only-empty')}
                  />
                  <span>Endast tomma fält (standard)</span>
                </S.RadioRow>
                <S.RadioRow>
                  <input
                    type="radio"
                    name="overwriteMode"
                    value="overwrite-all"
                    checked={overwriteMode === 'overwrite-all'}
                    onChange={() => setOverwriteMode('overwrite-all')}
                  />
                  <span>Skriv över alla genererade fält</span>
                </S.RadioRow>
              </S.OverwriteRow>
            </S.Section>

            <S.Section>
              <S.H6>Fält som skrivs ({plan.writes.length})</S.H6>
              {plan.writes.length === 0 ? (
                <S.EmptyState>Inga fält kommer att skrivas.</S.EmptyState>
              ) : (
                <S.List>
                  {plan.writes.map((entry) => (
                    <li key={entry.fieldId}>{entry.label}</li>
                  ))}
                </S.List>
              )}
            </S.Section>

            <S.Section>
              <S.H6>Fält som hoppas över ({plan.skips.length})</S.H6>
              {plan.skips.length === 0 ? (
                <S.EmptyState>Inga fält hoppas över.</S.EmptyState>
              ) : (
                <S.List>
                  {plan.skips.map((entry) => (
                    <li key={`${entry.fieldId}-${entry.reason}`}>
                      {entry.label}{' '}
                      <S.SkipReason>({entry.reasonLabel})</S.SkipReason>
                    </li>
                  ))}
                </S.List>
              )}
            </S.Section>

            <S.Section>
              <S.H6>Förhandsvisning</S.H6>
              {plan.writes.length === 0 ? (
                <S.EmptyState>Ingen förhandsvisning att visa.</S.EmptyState>
              ) : (
                plan.writes.map((entry) => {
                  const isOpen = Boolean(expandedPreviews[entry.fieldId]);
                  return (
                    <S.PreviewItem key={`preview-${entry.fieldId}`}>
                      <S.PreviewToggle
                        type="button"
                        onClick={() => togglePreview(entry.fieldId)}
                        aria-expanded={isOpen}
                      >
                        <S.B2>{entry.label}</S.B2>
                        <S.Icon
                          src={isOpen ? 'ChevronUp' : 'ChevronDown'}
                          width={16}
                          height={16}
                        />
                      </S.PreviewToggle>
                      {isOpen && (
                        <S.PreviewBody
                          dangerouslySetInnerHTML={{ __html: entry.html }}
                        />
                      )}
                    </S.PreviewItem>
                  );
                })
              )}
            </S.Section>

            <S.ContentFooter>
              <S.Button $outlined $width={180} onClick={props.onClose}>
                Avbryt
              </S.Button>
              <S.Button
                $width={180}
                onClick={handleConfirm}
                disabled={plan.writes.length === 0}
              >
                Bekräfta
              </S.Button>
            </S.ContentFooter>
          </S.Content>
        </S.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
